import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";
import {
  AbsenceRequestModal,
  ITTicketModal,
  FamilyMemberModal,
  AccidentModal,
  KmLogModal,
  KmHistoryModal,
  OfferDeclarationModal,
  DAAModal,
  EditFamilyModal,
  FineDetailModal,
  FineResponseModal
} from "../../components/portal/modals";
import {
  VehiclesTab,
  BenefitsTab,
  FamilyTab,
  FinesTab,
  OverviewTab,
  OffersTab,
  AbsencesTab,
  TrainingsTab,
  EquipmentTab,
  ITTicketsTab,
  ProfileTab
} from "../../components/portal/tabs";

const EmployeeSelfServicePortal = () => {
  const [loading, setLoading] = useState(true);
  const [employeeData, setEmployeeData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [absences, setAbsences] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [itTickets, setItTickets] = useState([]);
  const [fines, setFines] = useState([]);
  const [offers, setOffers] = useState([]);
  const [benefits, setBenefits] = useState({ benefits: [], family_members: [], total_monthly_value: 0 });
  const [accidents, setAccidents] = useState([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showKmModal, setShowKmModal] = useState(false);
  const [showKmHistoryModal, setShowKmHistoryModal] = useState(false);
  const [showFineResponseModal, setShowFineResponseModal] = useState(false);
  const [showFineDetailModal, setShowFineDetailModal] = useState(false);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [showAccidentModal, setShowAccidentModal] = useState(false);
  const [showDAAModal, setShowDAAModal] = useState(false);
  const [showConfirmAttendanceModal, setShowConfirmAttendanceModal] = useState(false);
  const [showEditFamilyModal, setShowEditFamilyModal] = useState(false);
  const [selectedAccident, setSelectedAccident] = useState(null);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [selectedFamilyMember, setSelectedFamilyMember] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedFine, setSelectedFine] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [selectedKmLog, setSelectedKmLog] = useState(null);
  const [kmLogs, setKmLogs] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('portal_theme');
    return saved ? saved === 'dark' : true;
  });
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('portal_theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Build recent activity from all data sources
  const getRecentActivity = () => {
    const activities = [];
    
    // Add absences
    absences.forEach(absence => {
      activities.push({
        type: 'absence',
        icon: '📅',
        title: `Pedido de Ausência - ${absence.absence_type || 'Férias'}`,
        description: `${absence.start_date} a ${absence.end_date}`,
        status: absence.status,
        date: absence.created_at || absence.start_date
      });
    });
    
    // Add trainings
    trainings.forEach(training => {
      activities.push({
        type: 'training',
        icon: '🎓',
        title: training.title,
        description: `Data: ${training.start_date}${training.mandatory ? ' • Obrigatória' : ''}`,
        status: training.status,
        date: training.created_at || training.start_date
      });
    });
    
    // Add offers
    offers.forEach(offer => {
      activities.push({
        type: 'offer',
        icon: offer.direction === 'received' ? '📥' : '📤',
        title: `Oferta ${offer.direction === 'received' ? 'Recebida' : 'Oferecida'}`,
        description: `${offer.offered_by || offer.related_entity_name || '-'} • ${offer.value || 0}€`,
        status: offer.status,
        date: offer.created_at || offer.offer_date
      });
    });
    
    // Add IT tickets
    itTickets.forEach(ticket => {
      activities.push({
        type: 'ticket',
        icon: '🎫',
        title: `Ticket IT - ${ticket.title || ticket.ticket_type}`,
        description: ticket.description?.substring(0, 50) + '...' || 'Suporte técnico',
        status: ticket.status,
        date: ticket.created_at
      });
    });
    
    // Add fines
    fines.forEach(fine => {
      activities.push({
        type: 'fine',
        icon: '⚠️',
        title: `Coima - ${fine.infraction_type || 'Infração'}`,
        description: `${fine.amount || 0}€ • ${fine.fine_date || '-'}`,
        status: fine.status,
        date: fine.created_at || fine.fine_date
      });
    });
    
    // Add vehicle km logs
    kmLogs.slice(0, 5).forEach(log => {
      activities.push({
        type: 'km_log',
        icon: '🚗',
        title: 'Registo de Quilómetros',
        description: `${log.km_difference || 0} km • ${log.destination || 'Viagem'}`,
        status: 'completed',
        date: log.created_at || log.date
      });
    });
    
    // Sort by date (most recent first) and return top 10
    return activities
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
      .slice(0, 10);
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      // Fetch employee profile
      const employeesRes = await api.get('/employees');
      const myEmployee = employeesRes.data.find(e => e.email === user?.email);
      setEmployeeData(myEmployee);

      if (myEmployee) {
        // Fetch absences - use portal endpoint
        try {
          const absencesRes = await api.get('/portal/my-absences');
          setAbsences(absencesRes.data);
        } catch (e) {
          // Fallback to HR endpoint if portal endpoint doesn't exist
          try {
            const absencesRes = await api.get('/hr/absences');
            setAbsences(absencesRes.data.filter(a => a.employee_id === myEmployee.employee_id));
          } catch (err) {
            console.log('Absences endpoint not available');
          }
        }

        // Fetch trainings
        const trainingsRes = await api.get('/hr/trainings');
        setTrainings(trainingsRes.data.filter(t => t.employee_id === myEmployee.employee_id));

        // Fetch equipment
        const equipmentRes = await api.get(`/hr/equipment/employee/${myEmployee.employee_id}`);
        setEquipment(equipmentRes.data);

        // Fetch vehicles
        const vehiclesRes = await api.get(`/hr/vehicles/employee/${myEmployee.employee_id}`);
        setVehicles(vehiclesRes.data);

        // Fetch family members
        try {
          const familyRes = await api.get('/portal/family');
          setFamilyMembers(familyRes.data);
        } catch (e) {
          console.log('Family endpoint not available');
        }

        // Fetch IT tickets
        try {
          const ticketsRes = await api.get('/portal/it-tickets');
          setItTickets(ticketsRes.data);
        } catch (e) {
          console.log('IT tickets endpoint not available');
        }

        // Fetch fines
        try {
          const finesRes = await api.get('/portal/my-fines');
          setFines(finesRes.data);
        } catch (e) {
          console.log('Fines endpoint not available');
        }

        // Fetch offers
        try {
          const offersRes = await api.get('/portal/my-offers');
          setOffers(offersRes.data);
        } catch (e) {
          console.log('Offers endpoint not available');
        }

        // Fetch benefits
        try {
          const benefitsRes = await api.get('/portal/benefits');
          setBenefits(benefitsRes.data);
        } catch (e) {
          console.log('Benefits endpoint not available');
        }

        // Fetch dashboard data
        try {
          const dashboardRes = await api.get('/portal/dashboard');
          setDashboardData(dashboardRes.data);
        } catch (e) {
          console.log('Dashboard endpoint not available');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Confirm attendance function
  const handleConfirmAttendance = async (trainingId) => {
    try {
      await api.post(`/hr/trainings/${trainingId}/confirm-attendance`, {
        confirmation_method: 'digital'
      });
      toast.success('Presença confirmada com sucesso (ACT)');
      setShowConfirmAttendanceModal(false);
      fetchAllData();
    } catch (error) {
      console.error('Error confirming attendance:', error);
      toast.error(error.response?.data?.detail || 'Erro ao confirmar presença');
    }
  };

  // Register accident function
  const handleRegisterAccident = async (vehicleId, accidentData) => {
    try {
      await api.post(`/hr/vehicles/${vehicleId}/accidents`, accidentData);
      toast.success('Sinistro registado com sucesso');
      setShowAccidentModal(false);
      fetchAllData();
    } catch (error) {
      console.error('Error registering accident:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registar sinistro');
    }
  };

  // Upload DAA function
  const handleUploadDAA = async (vehicleId, accidentId, daaData) => {
    try {
      await api.post(`/hr/vehicles/${vehicleId}/accidents/${accidentId}/daa`, daaData);
      toast.success('DAA carregada com sucesso');
      setShowDAAModal(false);
      fetchAllData();
    } catch (error) {
      console.error('Error uploading DAA:', error);
      toast.error(error.response?.data?.detail || 'Erro ao carregar DAA');
    }
  };

  // Update family member function
  const handleUpdateFamilyMember = async (memberId, memberData) => {
    try {
      await api.put(`/portal/family/${memberId}`, memberData);
      toast.success('Membro atualizado com sucesso');
      setShowEditFamilyModal(false);
      setSelectedFamilyMember(null);
      fetchAllData();
    } catch (error) {
      console.error('Error updating family member:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar membro');
    }
  };

  // Delete family member function
  const handleDeleteFamilyMember = async (memberId) => {
    if (!window.confirm('Tem certeza que deseja remover este membro?')) return;
    try {
      await api.delete(`/portal/family/${memberId}`);
      toast.success('Membro removido com sucesso');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting family member:', error);
      toast.error(error.response?.data?.detail || 'Erro ao remover membro');
    }
  };

  // Calculate age from birth date
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      manager_approved: 'bg-blue-100 text-blue-700',
      hr_approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      scheduled: 'bg-blue-100 text-blue-700',
      completed: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      pending: 'Pendente',
      manager_approved: 'Aprovado (Chefia)',
      hr_approved: 'Aprovado',
      rejected: 'Rejeitado',
      scheduled: 'Agendada',
      completed: 'Concluída'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-slate-100 via-white to-slate-100'}`}>
      {/* Header */}
      <header className={`${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white/80 border-slate-200'} border-b backdrop-blur-lg sticky top-0 z-50`}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/1ed57a29-834c-4440-94bb-8fa514a38873/images/893d9cb746463cc17aacfefa048bcc640ffd2f5ff4ab77b7e5239f606122dde9.png" 
              alt="CogniBiz" 
              className="h-10 w-10 object-contain" 
            />
            <div>
              <h1 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>Portal do Colaborador</h1>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Self-Service</p>
            </div>
          </div>
          
          {/* Right side with theme toggle and profile menu */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-lg transition-colors ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-yellow-400' : 'bg-slate-200 hover:bg-slate-300 text-slate-600'}`}
              data-testid="theme-toggle-btn"
              title={darkMode ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
            >
              {darkMode ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            {/* Profile dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-200'}`}
                data-testid="profile-menu-btn"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="text-left hidden sm:block">
                  <p className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-slate-800'}`}>{user?.name}</p>
                  <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{employeeData?.position || employeeData?.department || 'Colaborador'}</p>
                </div>
                <svg className={`w-4 h-4 transition-transform ${showProfileMenu ? 'rotate-180' : ''} ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown menu */}
              {showProfileMenu && (
                <div className={`absolute right-0 mt-2 w-56 rounded-xl shadow-xl overflow-hidden z-50 ${darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                  <div className={`p-4 border-b ${darkMode ? 'border-slate-700 bg-slate-700/50' : 'border-slate-200 bg-slate-50'}`}>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>{user?.name}</p>
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{employeeData?.email || user?.email}</p>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{employeeData?.employee_number || employeeData?.employee_id}</p>
                  </div>
                  <div className="py-2">
                    <button
                      onClick={() => { setActiveTab('profile'); setShowProfileMenu(false); }}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 ${darkMode ? 'text-slate-300 hover:bg-slate-700 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      O Meu Perfil
                    </button>
                    <button
                      onClick={() => { setActiveTab('family'); setShowProfileMenu(false); }}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 ${darkMode ? 'text-slate-300 hover:bg-slate-700 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Agregado Familiar
                    </button>
                    <button
                      onClick={() => { setActiveTab('absences'); setShowProfileMenu(false); }}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 ${darkMode ? 'text-slate-300 hover:bg-slate-700 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Férias & Ausências
                    </button>
                  </div>
                  <div className={`border-t py-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <button
                      onClick={logout}
                      className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-900/30 hover:text-red-300 flex items-center gap-3"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sair
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 mb-8 text-white">
          <h2 className="text-3xl font-bold mb-2">Olá, {user?.name?.split(' ')[0]}!</h2>
          <p className="text-blue-100">Bem-vindo ao portal de self-service. Aqui pode gerir as suas informações pessoais, ausências e muito mais.</p>
        </div>

        {/* Dashboard Metrics - Clickable Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <button 
            onClick={() => setActiveTab('absences')}
            className={`rounded-xl p-4 border transition-all text-left ${darkMode ? 'bg-slate-800 border-slate-700 hover:bg-slate-700 hover:border-blue-500' : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-blue-400 shadow-sm'}`}
            data-testid="dashboard-card-ferias"
          >
            <p className="text-2xl font-bold text-emerald-500">{dashboardData?.vacation_balance?.available || 22}</p>
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Férias Disponíveis</p>
            <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{dashboardData?.vacation_balance?.used || 0} utilizados</p>
          </button>
          <button 
            onClick={() => setActiveTab('trainings')}
            className={`rounded-xl p-4 border transition-all text-left ${darkMode ? 'bg-slate-800 border-slate-700 hover:bg-slate-700 hover:border-amber-500' : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-amber-400 shadow-sm'}`}
            data-testid="dashboard-card-formacoes"
          >
            <p className="text-2xl font-bold text-amber-500">{dashboardData?.mandatory_trainings_count || trainings.filter(t => t.mandatory).length}</p>
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Formações Obrigatórias</p>
          </button>
          <button 
            onClick={() => setActiveTab('equipment')}
            className={`rounded-xl p-4 border transition-all text-left ${darkMode ? 'bg-slate-800 border-slate-700 hover:bg-slate-700 hover:border-blue-500' : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-blue-400 shadow-sm'}`}
            data-testid="dashboard-card-equipamentos"
          >
            <p className="text-2xl font-bold text-blue-500">{dashboardData?.equipment_count || equipment.length}</p>
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Equipamentos</p>
          </button>
          <button 
            onClick={() => setActiveTab('vehicles')}
            className={`rounded-xl p-4 border transition-all text-left ${darkMode ? 'bg-slate-800 border-slate-700 hover:bg-slate-700 hover:border-purple-500' : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-purple-400 shadow-sm'}`}
            data-testid="dashboard-card-viaturas"
          >
            <p className="text-2xl font-bold text-purple-500">{vehicles.length}</p>
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Viaturas</p>
          </button>
          <button 
            onClick={() => setActiveTab('fines')}
            className={`rounded-xl p-4 border transition-all text-left ${darkMode ? 'bg-slate-800 border-slate-700 hover:bg-slate-700 hover:border-red-500' : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-red-400 shadow-sm'}`}
            data-testid="dashboard-card-coimas"
          >
            <p className={`text-2xl font-bold ${(dashboardData?.pending_fines || fines.filter(f => f.status === 'pending_response').length) > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
              {dashboardData?.pending_fines || fines.filter(f => f.status === 'pending_response').length}
            </p>
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Coimas Pendentes</p>
          </button>
          <button 
            onClick={() => setActiveTab('it-tickets')}
            className={`rounded-xl p-4 border transition-all text-left ${darkMode ? 'bg-slate-800 border-slate-700 hover:bg-slate-700 hover:border-cyan-500' : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-cyan-400 shadow-sm'}`}
            data-testid="dashboard-card-tickets"
          >
            <p className="text-2xl font-bold text-cyan-500">{dashboardData?.open_tickets || itTickets.filter(t => t.status === 'open').length}</p>
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Tickets Abertos</p>
          </button>
        </div>

        {/* Tabs - Single organized line */}
        <div className="mb-6 overflow-x-auto">
          <div className={`flex gap-1 min-w-max p-1 rounded-xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
            {[
              { id: 'overview', label: 'Visão Geral', icon: '📊' },
              { id: 'absences', label: 'Ausências', icon: '📅' },
              { id: 'trainings', label: 'Formações', icon: '🎓' },
              { id: 'equipment', label: 'Equipamentos', icon: '💻' },
              { id: 'vehicles', label: 'Viaturas', icon: '🚗' },
              { id: 'fines', label: 'Coimas', icon: '⚠️' },
              { id: 'it-tickets', label: 'Suporte IT', icon: '🎫' },
              { id: 'offers', label: 'Ofertas', icon: '🎁' },
              { id: 'benefits', label: 'Benefícios', icon: '💰' },
              { id: 'family', label: 'Família', icon: '👨‍👩‍👧‍👦' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 rounded-lg flex items-center gap-2 transition-all whitespace-nowrap text-sm font-medium ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                    : darkMode 
                      ? 'text-slate-400 hover:bg-slate-700 hover:text-white'
                      : 'text-slate-600 hover:bg-slate-200 hover:text-slate-800'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <span className="text-base">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className={`rounded-2xl border p-6 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <OverviewTab
              getRecentActivity={getRecentActivity}
              getStatusBadge={getStatusBadge}
              setActiveTab={setActiveTab}
              absences={absences}
              trainings={trainings}
              offers={offers}
              itTickets={itTickets}
              darkMode={darkMode}
            />
          )}

          {/* Offers Tab */}
          {activeTab === 'offers' && (
            <OffersTab offers={offers} setShowOfferModal={setShowOfferModal} darkMode={darkMode} />
          )}

          {/* Absences Tab */}
          {activeTab === 'absences' && (
            <AbsencesTab absences={absences} setShowAbsenceModal={setShowAbsenceModal} darkMode={darkMode} />
          )}

          {/* Trainings Tab */}
          {activeTab === 'trainings' && (
            <TrainingsTab
              trainings={trainings}
              getStatusBadge={getStatusBadge}
              handleConfirmAttendance={handleConfirmAttendance}
              darkMode={darkMode}
            />
          )}

          {/* Equipment Tab */}
          {activeTab === 'equipment' && (
            <EquipmentTab
              equipment={equipment}
              setSelectedEquipment={setSelectedEquipment}
              setShowTicketModal={setShowTicketModal}
              darkMode={darkMode}
            />
          )}

          {/* IT Tickets Tab */}
          {activeTab === 'it-tickets' && (
            <ITTicketsTab
              itTickets={itTickets}
              setSelectedEquipment={setSelectedEquipment}
              setShowTicketModal={setShowTicketModal}
              darkMode={darkMode}
            />
          )}

          {/* Vehicles Tab */}
          {activeTab === 'vehicles' && (
            <VehiclesTab
              vehicles={vehicles}
              setSelectedVehicle={setSelectedVehicle}
              setShowKmModal={setShowKmModal}
              setKmLogs={setKmLogs}
              setShowKmHistoryModal={setShowKmHistoryModal}
              setShowAccidentModal={setShowAccidentModal}
              setSelectedAccident={setSelectedAccident}
              setShowDAAModal={setShowDAAModal}
              darkMode={darkMode}
            />
          )}

          {/* Fines Tab */}
          {activeTab === 'fines' && (
            <FinesTab
              fines={fines}
              setSelectedFine={setSelectedFine}
              setShowFineDetailModal={setShowFineDetailModal}
              setShowFineResponseModal={setShowFineResponseModal}
              darkMode={darkMode}
            />
          )}

          {/* Benefits Tab */}
          {activeTab === 'benefits' && (
            <BenefitsTab benefits={benefits} darkMode={darkMode} />
          )}

          {/* Family Tab */}
          {activeTab === 'family' && (
            <FamilyTab
              familyMembers={familyMembers}
              calculateAge={calculateAge}
              setShowFamilyModal={setShowFamilyModal}
              setSelectedFamilyMember={setSelectedFamilyMember}
              setShowEditFamilyModal={setShowEditFamilyModal}
              handleDeleteFamilyMember={handleDeleteFamilyMember}
              darkMode={darkMode}
            />
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <ProfileTab employeeData={employeeData} darkMode={darkMode} />
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-4 py-8 text-center text-slate-500 text-sm">
        <p>© {new Date().getFullYear()} CogniBiz - Know your Business. Portal Self-Service.</p>
      </footer>

      {/* IT Ticket Modal */}
      {showTicketModal && (
        <ITTicketModal
          equipment={selectedEquipment}
          equipmentList={equipment}
          onClose={() => { setShowTicketModal(false); setSelectedEquipment(null); }}
          onSave={async (data) => {
            try {
              await api.post('/portal/it-tickets', data);
              toast.success('Ticket criado com sucesso');
              const res = await api.get('/portal/it-tickets');
              setItTickets(res.data);
              setShowTicketModal(false);
              setSelectedEquipment(null);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Erro ao criar ticket');
            }
          }}
        />
      )}

      {/* KM Log Modal */}
      {showKmModal && selectedVehicle && (
        <KmLogModal
          vehicle={selectedVehicle}
          onClose={() => { setShowKmModal(false); setSelectedVehicle(null); }}
          onSave={async (data) => {
            try {
              await api.post(`/portal/vehicles/${selectedVehicle.vehicle_id}/km-logs`, data);
              toast.success('Quilómetros registados');
              // Refresh vehicles
              const vehiclesRes = await api.get(`/hr/vehicles/employee/${employeeData.employee_id}`);
              setVehicles(vehiclesRes.data);
              setShowKmModal(false);
              setSelectedVehicle(null);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Erro ao registar KM');
            }
          }}
        />
      )}

      {/* KM History Modal */}
      {showKmHistoryModal && selectedVehicle && (
        <KmHistoryModal
          vehicle={selectedVehicle}
          logs={kmLogs}
          darkMode={darkMode}
          onClose={() => { setShowKmHistoryModal(false); setSelectedVehicle(null); setKmLogs([]); }}
          onViewLog={(log) => setSelectedKmLog(log)}
        />
      )}

      {/* Fine Response Modal */}
      {/* Fine Detail Modal */}
      {showFineDetailModal && selectedFine && (
        <FineDetailModal
          fine={selectedFine}
          darkMode={darkMode}
          onClose={() => { setShowFineDetailModal(false); setSelectedFine(null); }}
          onRespond={() => { setShowFineDetailModal(false); setShowFineResponseModal(true); }}
        />
      )}

      {showFineResponseModal && selectedFine && (
        <FineResponseModal
          fine={selectedFine}
          employeeData={employeeData}
          onClose={() => { setShowFineResponseModal(false); setSelectedFine(null); }}
          onSave={async (data) => {
            try {
              await api.post(`/portal/fines/${selectedFine.fine_id}/respond`, data);
              toast.success('Resposta registada com sucesso');
              const finesRes = await api.get('/portal/my-fines');
              setFines(finesRes.data);
              setShowFineResponseModal(false);
              setSelectedFine(null);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Erro ao registar resposta');
            }
          }}
        />
      )}

      {/* Family Member Modal */}
      {showFamilyModal && (
        <FamilyMemberModal
          onClose={() => setShowFamilyModal(false)}
          onSave={async (data) => {
            try {
              await api.post('/portal/family', data);
              toast.success('Membro adicionado');
              const familyRes = await api.get('/portal/family');
              setFamilyMembers(familyRes.data);
              setShowFamilyModal(false);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Erro ao adicionar membro');
            }
          }}
        />
      )}

      {/* Offer Declaration Modal */}
      {showOfferModal && (
        <OfferDeclarationModal
          employee={employeeData}
          onClose={() => setShowOfferModal(false)}
          onSave={async (data) => {
            try {
              await api.post('/portal/offers', data);
              toast.success('Oferta declarada com sucesso');
              const offersRes = await api.get('/portal/my-offers');
              setOffers(offersRes.data);
              setShowOfferModal(false);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Erro ao declarar oferta');
            }
          }}
        />
      )}

      {/* Absence Request Modal */}
      {showAbsenceModal && (
        <AbsenceRequestModal
          darkMode={darkMode}
          onClose={() => setShowAbsenceModal(false)}
          onSave={async (data) => {
            try {
              await api.post('/portal/absences', data);
              toast.success('Pedido de ausência submetido com sucesso');
              // Refresh absences list
              try {
                const absencesRes = await api.get('/portal/my-absences');
                setAbsences(absencesRes.data);
              } catch (e) {
                // Try the hr endpoint as fallback
                const absencesRes = await api.get('/hr/absences/my');
                setAbsences(absencesRes.data);
              }
              setShowAbsenceModal(false);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Erro ao submeter pedido');
            }
          }}
        />
      )}

      {/* Accident Registration Modal */}
      {showAccidentModal && selectedVehicle && (
        <AccidentModal
          vehicle={selectedVehicle}
          onClose={() => { setShowAccidentModal(false); setSelectedVehicle(null); }}
          onSave={handleRegisterAccident}
        />
      )}

      {/* DAA Upload Modal */}
      {showDAAModal && selectedVehicle && selectedAccident && (
        <DAAModal
          vehicle={selectedVehicle}
          accident={selectedAccident}
          onClose={() => { setShowDAAModal(false); setSelectedAccident(null); }}
          onSave={handleUploadDAA}
        />
      )}

      {/* Edit Family Member Modal */}
      {showEditFamilyModal && selectedFamilyMember && (
        <EditFamilyModal
          member={selectedFamilyMember}
          onClose={() => { setShowEditFamilyModal(false); setSelectedFamilyMember(null); }}
          onSave={handleUpdateFamilyMember}
        />
      )}
    </div>
  );
};

export default EmployeeSelfServicePortal;
